import {Icon} from '@wandb/components';
import {NavLink} from '@wandb/weave/common/util/links';
import React from 'react';

import {useViewer} from '../../../state/viewer/hooks';
import {type Viewer} from '../../../state/viewer/types';
import {WORKSPACE_PLACEHOLDER_NAME} from '../../../state/workspaces/displayNames';
import {
  namedWorkspaceBelongsToUser,
  type NamedWorkspaceId,
} from '../../../state/workspaces/types';
import {getNewNwUrlSearch, ParamNames} from '../../../state/workspaces/url';
import {DateFormat, format, getLocalYear} from '../../../util/date';
import {type NamedWorkspaceMenuItem} from '../types';
import {isUnnamedWorkspace} from '../utils';
import {UserProfileImage} from './UserProfileImage';

export type NamedWorkspaceMenuItemProps = {
  option: NamedWorkspaceMenuItem;
};

export const NamedWorkspaceMenuItemComp = ({
  option,
}: NamedWorkspaceMenuItemProps) => {
  const viewer = useViewer();
  const isViewerPersonalWorkspace = namedWorkspaceBelongsToUser(
    option.namedWorkspaceId,
    viewer
  );

  return (
    <NavLink
      className="flex grow text-wb-text-primary dark:text-moon-300"
      to={getMenuItemLinkTo(option.namedWorkspaceId)}>
      {isViewerPersonalWorkspace ? (
        <div className="flex h-24 w-24 shrink-0 items-center justify-center border rounded-full border-wb-border bg-wb-surface-tint">
          <Icon icon="home" size="sm" />
        </div>
      ) : (
        <UserProfileImage
          src={option.photoUrl}
          alt={`${option.createdBy.username}'s profile`}
        />
      )}
      <NamedWorkspaceMenuItemContent option={option} />
    </NavLink>
  );
};

export const NamedWorkspaceMenuItemContent = ({
  option,
  showEditLock = true,
}: NamedWorkspaceMenuItemProps & {showEditLock?: boolean}) => {
  const viewer = useViewer();
  const isShared = option.optionType === 'shared';
  const lastSavedUserDisplayName = getUserDisplayName(
    viewer,
    option.updatedBy,
    option.createdBy
  );

  return (
    <div
      className="mx-8 flex flex-col p-0 text-base leading-6"
      data-test={isShared ? 'named-workspace-menu-item' : undefined}>
      <div className="flex min-w-0 grow items-center gap-6">
        <p className="max-w-[13rem] min-w-0 overflow-hidden text-ellipsis whitespace-nowrap text-wb-text-primary dark:text-moon-300">
          {isShared && isUnnamedWorkspace(option.displayName)
            ? WORKSPACE_PLACEHOLDER_NAME
            : option.displayName}
        </p>
        {showEditLock && option.isEditLocked && (
          <Icon
            icon="lock-closed"
            size="xs"
            aria-label="Only the owner can edit this view"
          />
        )}
      </div>
      {option.updatedAt != null && (
        <p className="shrink-0 text-sm text-wb-text-tertiary">
          Saved on {formatDate(option.updatedAt)}{' '}
          {isShared &&
            lastSavedUserDisplayName !== '' &&
            `by ${lastSavedUserDisplayName}`}
        </p>
      )}
    </div>
  );
};

function getMenuItemLinkTo(namedWorkspaceId: NamedWorkspaceId) {
  return getNewNwUrlSearch({
    [ParamNames.nwId]: namedWorkspaceId.id,
  });
}

function getUserDisplayName(
  viewer: Viewer | undefined,
  updatedBy: NamedWorkspaceMenuItem['updatedBy'],
  createdBy: NamedWorkspaceMenuItem['createdBy']
) {
  const updatedByDisplayName = getDisplayName(viewer, updatedBy?.username);
  if (updatedByDisplayName == null || updatedByDisplayName === '') {
    // fall back to createdBy username because someone might not have
    // clickked on "Save view" yet
    return getDisplayName(viewer, createdBy?.username);
  }
  return updatedByDisplayName;
}
function getDisplayName(
  viewer: Viewer | undefined,
  username: string | undefined
) {
  if (username == null) {
    return '';
  }
  if (username === viewer?.username) {
    return 'you';
  }
  return username;
}

/**
 * Example of formatted date:
 * Nov 03 at 5:09pm OR Nov 03, 23 at 5:09pm
 */
export function formatDate(d: string | Date) {
  const date = new Date(d);
  const time = ' at ' + format(date, DateFormat.TIME);

  if (getLocalYear(date) !== getLocalYear(new Date())) {
    return format(date, DateFormat.MONTH_DAY_SHORT_YEAR) + time;
  }

  return format(date, DateFormat.MONTH_DAY) + time;
}
